"use client";

/**
 * Üye presence aboneliği — kaynak-referans `state/MemberPresenceSubscription` portu.
 *
 * Amaç: bir sunucu (guild) içinde presence'ını takip etmek istediğimiz üye
 * kümesini sunucu (guild) başına LRU + TTL ile tutmak. kaynak-referansta bu küme
 * gateway'e "şu üyelerin presence'ını bana gönder" olarak push edilir
 * (socket.updateGuildSubscriptions). TEBHOS :9000'te bu abonelik ucu YOK —
 * bu yüzden depo CLIENT-ONLY tutulur: LRU/TTL/debounce mantığı birebir
 * korunur, gerçek gateway push'u ise değiştirilebilir bir sync handler
 * (`setSyncHandler`) arkasına alınır. :9000 aktifken (varsayılan) handler
 * hiç enjekte edilmez → no-op, davranış AYNEN. :9100 (Erlang v2, flag-gated)
 * aktifken ws-client.ts (`connectV2`) handler'ı enjekte eder ve gerçek op14
 * LAZY_REQUEST push'u (GatewayV2Socket.updateGuildSubscriptions) devreye
 * girer — bağlantı kapanınca (`disconnect`) handler tekrar söker.
 *
 * zustand→mobx dönüşümü: kaynak-referanstaki mobx sınıf deseni birebir geri alındı —
 * `Map<guildId, Map<userId, lastAccessed>>` iç durum + private timer alanları
 * gözlemlenebilirlik dışı (`makeAutoObservable` override), reaktif yüzey ise
 * kaynak-referanstaki gibi tek `subscriptionVersion` sayacıdır. Metot yüzeyi
 * (touchMember / unsubscribe / setActiveGuild / getSubscribedMembers /
 * clearGuild / clearAll) kaynak-referans ile aynıdır; `setSyncHandler` TEBHOS eki.
 *
 * Backend bağımlılığı: yalnız :9100 (flag-gated, opt-in deneme yolu) altında;
 * :9000 tamamen client-side kalır.
 */

import { makeAutoObservable, runInAction } from "mobx";

const MEMBER_SUBSCRIPTION_MAX_SIZE = 100;
const MEMBER_SUBSCRIPTION_TTL_MS = 5 * 60 * 1000;
const MEMBER_SUBSCRIPTION_SYNC_DEBOUNCE_MS = 500;
const MEMBER_SUBSCRIPTION_PRUNE_INTERVAL_MS = 30 * 1000;

interface LRUEntry {
  userId: string;
  lastAccessed: number;
}

/**
 * Gateway'e abonelik push'unun soyutlaması. kaynak-referanstaki
 * `socket.updateGuildSubscriptions` çağrılarının TEBHOS karşılığı; abonelik
 * ucu gelene kadar `setSyncHandler` ile enjekte edilmezse no-op çalışır.
 */
export interface MemberSubscriptionSyncHandler {
  /** Bir guild için abone üye listesini (sıralı) gönder. */
  syncMembers(guildId: string, members: Array<string>): void;
  /** Bir guild için aktiflik bayrağını gönder (görüntülenen sunucu). */
  syncActive?(guildId: string, active: boolean): void;
}

class MemberPresenceSubscription {
  /** guildId → (userId → lastAccessed ms). Gözlemlenebilirlik DIŞI (referans-parite). */
  private subscriptions: Map<string, Map<string, number>> = new Map();
  /** Şu an görüntülenen (aktif) sunucu — presence sync'i için işaretlenir. */
  private activeGuildId: string | null = null;
  private syncTimeoutId: ReturnType<typeof setTimeout> | null = null;
  private pruneIntervalId: ReturnType<typeof setInterval> | null = null;
  private pendingSyncGuilds: Set<string> = new Set();
  private syncHandler: MemberSubscriptionSyncHandler | null = null;
  /** Abonelik seti her değiştiğinde artan sürüm (tek reaktif alan). */
  subscriptionVersion = 0;

  constructor() {
    makeAutoObservable<
      this,
      "subscriptions" | "syncTimeoutId" | "pruneIntervalId" | "pendingSyncGuilds" | "syncHandler"
    >(
      this,
      {
        subscriptions: false,
        syncTimeoutId: false,
        pruneIntervalId: false,
        pendingSyncGuilds: false,
        syncHandler: false,
      },
      { autoBind: true }
    );
  }

  private startPruneInterval(): void {
    if (this.pruneIntervalId != null) {
      return;
    }
    this.pruneIntervalId = setInterval(() => {
      this.pruneExpiredEntries();
    }, MEMBER_SUBSCRIPTION_PRUNE_INTERVAL_MS);
  }

  private stopPruneInterval(): void {
    if (this.pruneIntervalId == null) {
      return;
    }
    clearInterval(this.pruneIntervalId);
    this.pruneIntervalId = null;
  }

  private syncPruneInterval(): void {
    if (this.subscriptions.size > 0) {
      this.startPruneInterval();
      return;
    }
    this.stopPruneInterval();
  }

  private getGuildSubscriptions(guildId: string): Map<string, number> {
    let guildSubs = this.subscriptions.get(guildId);
    if (!guildSubs) {
      guildSubs = new Map();
      this.subscriptions.set(guildId, guildSubs);
    }
    return guildSubs;
  }

  /** Bir üyeye eriş → LRU'da öne al, guild'i sync'e kuyrukla (debounce). */
  touchMember(guildId: string, userId: string): void {
    const guildSubs = this.getGuildSubscriptions(guildId);
    const now = Date.now();
    if (guildSubs.has(userId)) {
      guildSubs.delete(userId);
    }
    guildSubs.set(userId, now);
    this.enforceMaxSize(guildId);
    this.syncPruneInterval();
    this.scheduleSyncToGateway(guildId);
    this.bumpVersion();
  }

  /** Bir üyeyi abonelikten çıkar → guild'i hemen sync et. */
  unsubscribe(guildId: string, userId: string): void {
    const guildMembers = this.subscriptions.get(guildId);
    if (!guildMembers || !guildMembers.has(userId)) return;
    guildMembers.delete(userId);
    if (guildMembers.size === 0) {
      this.subscriptions.delete(guildId);
    }
    this.syncPruneInterval();
    this.bumpVersion();
    this.syncToGatewayImmediate(guildId);
  }

  /** Aktif (görüntülenen) sunucuyu değiştir. */
  setActiveGuild(guildId: string): void {
    if (this.activeGuildId === guildId) {
      return;
    }
    const previous = this.activeGuildId;
    this.activeGuildId = guildId;
    if (previous) {
      this.syncActiveFlagImmediate(previous, false);
    }
    this.syncActiveFlagImmediate(guildId, true);
    this.scheduleSyncToGateway(guildId);
    this.bumpVersion();
  }

  /** Bir guild'in abone üye id listesi. */
  getSubscribedMembers(guildId: string): Array<string> {
    const guildSubs = this.subscriptions.get(guildId);
    if (!guildSubs) {
      return [];
    }
    return Array.from(guildSubs.keys());
  }

  /** Bir guild'in tüm aboneliklerini temizle. */
  clearGuild(guildId: string): void {
    const hadSubscriptions = this.subscriptions.has(guildId);
    const wasActiveGuild = this.activeGuildId === guildId;
    this.subscriptions.delete(guildId);
    this.syncPruneInterval();
    if (hadSubscriptions) {
      this.syncToGatewayImmediate(guildId);
      this.bumpVersion();
    }
    if (wasActiveGuild) {
      this.activeGuildId = null;
      this.syncActiveFlagImmediate(guildId, false);
    }
  }

  /** Tüm abonelikleri ve aktif guild'i temizle (oturum kapanışı). */
  clearAll(): void {
    if (this.syncHandler) {
      for (const guildId of this.subscriptions.keys()) {
        this.syncHandler.syncMembers(guildId, []);
      }
      if (this.activeGuildId) {
        this.syncActiveFlagImmediate(this.activeGuildId, false);
      }
    }
    this.subscriptions.clear();
    this.activeGuildId = null;
    this.stopPruneInterval();
    if (this.syncTimeoutId != null) {
      clearTimeout(this.syncTimeoutId);
      this.syncTimeoutId = null;
    }
    this.pendingSyncGuilds.clear();
    this.bumpVersion();
  }

  /** Gateway push handler'ını enjekte et (ileride socket wiring için). */
  setSyncHandler(handler: MemberSubscriptionSyncHandler | null): void {
    this.syncHandler = handler;
  }

  /**
   * PRESENCE-DONMASI FIX (2026-07-28): mevcut TÜM abonelikleri gateway'e yeniden
   * gönderir. Abonelikler gateway tarafında OTURUM ÖMÜRLÜ tutuluyor (guild
   * sürecindeki `member_subscriptions`); yeni bir READY (reconnect / resume
   * başarısız) ya da guild sürecinin yeniden başlaması durumunda sunucu tarafı
   * boşalıyor, istemci ise LRU kayıtlarını "taze" sandığı için bir daha hiç
   * göndermiyordu → o istemci için üye-listesi presence'ı sessizce ölüyordu.
   * READY'de bu çağrıyla abonelik durumu yeniden kurulur.
   */
  resyncAll(): void {
    if (!this.syncHandler) {
      return;
    }
    if (this.activeGuildId) {
      this.syncActiveFlagImmediate(this.activeGuildId, true);
    }
    for (const guildId of this.subscriptions.keys()) {
      this.syncToGatewayImmediate(guildId);
    }
  }

  private bumpVersion(): void {
    runInAction(() => {
      this.subscriptionVersion++;
    });
  }

  // LRU: guild başına MAX_SIZE aşılırsa en eski erişilenleri at.
  private enforceMaxSize(guildId: string): void {
    const guildSubs = this.subscriptions.get(guildId);
    if (!guildSubs || guildSubs.size <= MEMBER_SUBSCRIPTION_MAX_SIZE) {
      return;
    }
    const entries: Array<LRUEntry> = [];
    for (const [userId, lastAccessed] of guildSubs) {
      entries.push({ userId, lastAccessed });
    }
    entries.sort((a, b) => a.lastAccessed - b.lastAccessed);
    const toRemove = entries.slice(0, entries.length - MEMBER_SUBSCRIPTION_MAX_SIZE);
    for (const entry of toRemove) {
      guildSubs.delete(entry.userId);
    }
  }

  private pruneExpiredEntries(): void {
    const now = Date.now();
    const cutoff = now - MEMBER_SUBSCRIPTION_TTL_MS;
    const guildsToSync = new Set<string>();
    for (const [guildId, guildSubs] of this.subscriptions) {
      const toRemove: Array<string> = [];
      for (const [userId, lastAccessed] of guildSubs) {
        if (lastAccessed < cutoff) {
          toRemove.push(userId);
        }
      }
      for (const userId of toRemove) {
        guildSubs.delete(userId);
        guildsToSync.add(guildId);
      }
      if (guildSubs.size === 0) {
        this.subscriptions.delete(guildId);
      }
    }
    this.syncPruneInterval();
    if (guildsToSync.size > 0) {
      for (const guildId of guildsToSync) {
        this.scheduleSyncToGateway(guildId);
      }
      this.bumpVersion();
    }
  }

  private scheduleSyncToGateway(guildId: string): void {
    this.pendingSyncGuilds.add(guildId);
    if (this.syncTimeoutId != null) {
      return;
    }
    this.syncTimeoutId = setTimeout(() => {
      this.syncTimeoutId = null;
      this.flushPendingSyncs();
    }, MEMBER_SUBSCRIPTION_SYNC_DEBOUNCE_MS);
  }

  private flushPendingSyncs(): void {
    const guildsToSync = Array.from(this.pendingSyncGuilds);
    this.pendingSyncGuilds.clear();
    for (const guildId of guildsToSync) {
      this.syncToGatewayImmediate(guildId);
    }
  }

  private syncActiveFlagImmediate(guildId: string, active: boolean): void {
    this.syncHandler?.syncActive?.(guildId, active);
  }

  private syncToGatewayImmediate(guildId: string): void {
    if (!this.syncHandler) {
      return;
    }
    const guildSubs = this.subscriptions.get(guildId);
    const members = guildSubs ? Array.from(guildSubs.keys()).sort() : [];
    this.syncHandler.syncMembers(guildId, members);
  }
}

export const MemberPresenceSubscriptionStore = new MemberPresenceSubscription();
